

import React, { useState, useEffect, useRef } from 'react';
import { Button, Modal, Form, Spinner, Offcanvas } from 'react-bootstrap';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { useLocation } from 'react-router-dom';
import withPermission from '../utils/withPermission';
import Swal from 'sweetalert2';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const API_URL = ` ${backendUrl}/api/documents`;

const DocumentDetails = React.forwardRef(({ instanceId, onModalStateChange, onShowModal, hideList = false, documentableType: propType }, ref) => {
  const [documents, setDocuments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [errors, setErrors] = useState({});
  const WithCreatePermission = withPermission(({ children }) => children, ['document-create']);
  const WithEditPermission = withPermission(({ children }) => children, ['document-edit']);
  const WithDeletePermission = withPermission(({ children }) => children, ['document-delete']);
  const [loading, setLoading] = useState(false); // Track loading state



  const [formData, setFormData] = useState({
    title: '',
    file: null,
    documentable_id: instanceId,
    documentable_type: '', // Will be set dynamically
  });
  const [isEdit, setIsEdit] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const location = useLocation();

  React.useImperativeHandle(ref, () => ({
    handleSubmit: () => handleSubmit(),
    handleManualInputChange: (e) => handleInputChange(e),
    handleManualFileChange: (e) => handleFileChange(e),
    getFormData: () => formData,
    setFormData: (data) => setFormData(data),
    setIsEdit: (edit) => setIsEdit(edit),
    setShowModal: (show) => setShowModal(show),
    setErrors: (err) => setErrors(err)
  }));

  useEffect(() => {
    onModalStateChange?.(showModal);
  }, [showModal, onModalStateChange]); // Hook to get the current pathname

  const didFetchRef = useRef(false);

  // Determine documentable_type dynamically based on the URL
  const getDocumentableType = () => {
    let type = '';
    if (propType) type = propType;
    else if (location.pathname.includes('/instance/')) {
      type = 'instance';
    } else if (location.pathname.includes('/tenant/')) {
      type = 'tenant';
    }
    else if (location.pathname.includes('/client/')) {
      type = 'client';
    }
    return type.toLowerCase();
  };

  // Fetch documents when the component is mounted
  useEffect(() => {
    if (!didFetchRef.current && !hideList) {
      fetchDocuments();
      didFetchRef.current = true;
    }
  }, [instanceId, hideList]);

  const fetchDocuments = async (page = 1) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');

      const token = userInfo?.token;
      const documentableType = getDocumentableType();

      const response = await axios.get(
        `${API_URL}?documentable_id=${instanceId}&type=${documentableType}&page=${page}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Tenant-Origin': origin,

          },
          skipLoader:true,
        });
      setDocuments(response.data.data);
      setPageCount(response.data.last_page);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleAddDocument = () => {
    const freshData = {
      title: '',
      file: null,
      documentable_id: instanceId,
      documentable_type: getDocumentableType(),  // Set dynamically
    };
    if (onShowModal) {
      onShowModal(false, freshData);
    } else {
      setFormData(freshData);
      setIsEdit(false);
      setSelectedDocument(null);
      setShowModal(true);
      setErrors({});
    }
  };




  const handleDownload = async (documentId, documentTitle) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      const response = await axios.get(` ${backendUrl}/api/documents/${documentId}`, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Origin': origin,
        },
        withCredentials: true,
      });

      const contentDisposition = response.headers['content-disposition'];
      let fileName = documentTitle;

      // Extract file name from headers
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) {
          fileName = match[1];
        }
      }

      // Create blob and download
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading the file:', error);
    }
  };





  const handleEditDocument = (document) => {

    setFormData({
      ...document,
      file: null,
      documentable_type: getDocumentableType(),  // Set dynamically for edit
    }); setIsEdit(true);
    setSelectedDocument(document);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setErrors({}); // Clear any residual error
  };
  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const userInfo = JSON.parse(localStorage.getItem('user-info'));
          const origin = localStorage.getItem('user-origin');
          const token = userInfo?.token;
          await axios.delete(`${API_URL}/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Tenant-Origin': origin,
            },
          });
          setDocuments(documents.filter((doc) => doc.id !== id));
          Swal.fire(
            "Deleted!",
            `The ${getDocumentableType() === 'tenant' ? 'Contracts' : 'Documents'} has been deleted.`,
            "success"
          );
        } catch (error) {
          console.error(`Error deleting ${getDocumentableType() === 'tenant' ? 'Contracts' : 'Documents'}:`, error);
          Swal.fire(
            "Error!",
            `Failed to delete ${getDocumentableType() === 'tenant' ? 'Contracts' : 'Documents'}. Please try again.`,
            "error"
          );
        }
      }
    });

  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prevErrors) => {
      const { [name]: removed, ...rest } = prevErrors; // Remove the key
      return rest;
    });
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };


  const handlePageChange = ({ selected }) => {
    const newPage = selected + 1;
    setCurrentPage(newPage);
    fetchDocuments(newPage);
  };
  useEffect(() => {
    if (!showModal) {
      setErrors({});
    }
  }, [showModal]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = 'title is required';
    // if (!formData.file) newErrors.file = 'file is required';
    return newErrors;
  };



  const handleSubmit = async () => {
    setLoading(true); // Start the button-specific loader
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return; // Stop here if validation fails
    }

    const formDataToSubmit = new FormData();
    formDataToSubmit.append('title', formData.title); // Add title

    if (formData.file) {
      formDataToSubmit.append('file', formData.file); // Add file only if it exists
    }

    formDataToSubmit.append('documentable_id', formData.documentable_id);
    const rawType = formData.documentable_type || propType || getDocumentableType();
    const typeValue = rawType?.toLowerCase() || '';
    formDataToSubmit.append('type', typeValue);
    formDataToSubmit.append('documentable_type', typeValue);

    try {
      const userInfo = JSON.parse(localStorage.getItem('user-info'));
      const origin = localStorage.getItem('user-origin');
      const token = userInfo?.token;

      let response;

      if (isEdit) {
        // Sending PUT request with FormData
        response = await axios.post(
          `${API_URL}/${selectedDocument.id}?_method=PUT`,
          formDataToSubmit,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data', // Ensure correct Content-Type
              'Tenant-Origin': origin,

            },
          }
        );


        // Update the document in the state
        setDocuments((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === selectedDocument.id ? response.data.document : doc
          )
        );
      } else {
        // Sending POST request for a new document
        response = await axios.post(API_URL, formDataToSubmit, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
            'Tenant-Origin': origin,
          },
        });

        setDocuments((prevDocs) => [...prevDocs, response.data.document]);
      }

      setErrors({});
      setShowModal(false);
      setFormData({
        title: '',
        file: null,
        documentable_id: instanceId,
        documentable_type: getDocumentableType(),
      });
    } catch (error) {
      {
        console.error("Unexpected error: ", error);
      }
    } finally {
      setLoading(false); // Stop the button-specific loader
    }

  };







  return (
    <div>
      {!hideList && (
        <div className="px-4 mt-3">
          <h4></h4><WithCreatePermission>
      <button
                                                    type="button"
                                                    className="btn rounded-circle border d-flex align-items-center justify-content-center round-create-button"
                                                   
                                                    onClick={handleAddDocument}                                                    data-bs-toggle="tooltip"
                                                    data-bs-placement="top"
                                                    id="tool"
                                                    title={`Add ${getDocumentableType() === 'tenant' ? 'Contract' : 'Document'}`}
                                                >
                                                    <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                                                </button>
        </WithCreatePermission><br/>
              <div className="contacts-table-modern">
                <div className="table-responsive">
                    <table className="table table-modern">
                        <thead>
                            
                                    <tr>
                                      <th scope="col" style={{ width: '60px' }}>Sl No</th>
                                      <th scope="col">Title</th>
                                      <th scope="col">File</th>
                                      <th scope="col">Actions</th>
                                    </tr>
        </thead>
        <tbody>
          {documents.map((document, index) => (
            <tr key={document.id}>
              <td className="text-muted">{index + 1}</td>
              <td>{document.title}</td>
              <td>
                <button
                  onClick={() => handleDownload(document.id, document.title)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'inherit',
                    textDecoration: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '14px'
                  }}
                >
                  Download
                </button>
              </td>


              <td>
                <div className="dropdown">
                  <i
                    className="mdi mdi-dots-horizontal font-size-18"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    style={{ cursor: 'pointer' }}
                  ></i>
                  <ul className="dropdown-menu dropdown-menu-end font-size-14">
                    <li><WithEditPermission>
                      <button className="dropdown-item" onClick={() => handleEditDocument(document)}>
                        Edit
                      </button></WithEditPermission>
                    </li>
                    <li><WithDeletePermission>
                      <button className="dropdown-item" onClick={() => handleDelete(document.id)}>
                        Delete
                      </button></WithDeletePermission>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div></div>
      <div className="d-flex justify-content-end pagination-sm">
      <ReactPaginate
                            previousLabel={'<'}
                            nextLabel={'>'}
                            breakLabel={'...'}
                            pageCount={pageCount}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={3}
                            onPageChange={handlePageChange}
                            containerClassName={'pagination pagination-rounded justify-content-end mb-2'}
                            pageClassName={'page-item'}
                            pageLinkClassName={'page-link'}
                            previousClassName={'page-item previous'}
                            previousLinkClassName={'page-link'}
                            nextClassName={'page-item next'}
                            nextLinkClassName={'page-link'}
                            breakClassName={'page-item'}
                            breakLinkClassName={'page-link'}
                            activeClassName={'active'}
                        />
      </div>
      </div>
      )}
      {/* Modal for Add/Edit Document */}
      {!onShowModal && (
        <>
   
            <Offcanvas show={showModal} onHide={handleCloseModal} placement='end' style={{ width: '650px'}}>  
              <Offcanvas.Header closeButton>
                <Offcanvas.Title>{isEdit ? `Edit ${getDocumentableType() === 'tenant' ? 'Contract' : 'Document'}` : `Add ${getDocumentableType() === 'tenant' ? 'Contract' : 'Document'}`}</Offcanvas.Title>
              </Offcanvas.Header>
              <hr style={{ margin: 0 }} /><br/>

              <Offcanvas.Body>
                <Form>
                  <Form.Group controlId="documentTitle" className="mb-3">
                    <Form.Label>Title</Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      className={`form-control ${errors?.title ? 'is-invalid' : ''}`}
                    />
                    {errors.title && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.title}</div>}
                  </Form.Group>
                  <Form.Group controlId="documentFile" className="mb-3">
                    <Form.Label>File</Form.Label>
                  <Form.Control
                    type="file"
                    name="file"
                    className={`form-control ${errors?.file ? 'is-invalid' : ''}`}
                    onChange={handleFileChange}
                  />
                  {errors.file && <div className="invalid-feedback" style={{ fontSize: '12px' }}>*{errors.file}</div>}
                  </Form.Group>
                </Form>
              </Offcanvas.Body>
              <div className="p-3 border-top  d-flex justify-content-end">
                <Button variant="secondary me-2" onClick={() => setShowModal(false)}>
                  Close
                </Button>
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                  {loading && (
                      <Spinner
                        animation="border"
                        size="sm"
                        style={{ marginRight: '10px' }} // Spacing between the spinner and text
                      />
                  )}
                  {isEdit ? 'Update' : 'Save'}
                </Button>
              </div>
            </Offcanvas>
        </>
      )}
    </div>
  );
});

export default DocumentDetails;

